import { useState, type RefObject } from "react";
import type { Editor } from "@tiptap/core";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { BlockInfo, StyleProperty } from "./documentEditor";
import { TextColorPicker } from "./TextColorPicker";
import { TEXT_SESSION_HOLD_PROPS } from "./textSessionFocus";
import {
  computedSelectionStyle,
  readTextStyleSelection,
} from "./textStyleMark";
import { useRenderedStyle } from "./useRenderedStyle";

interface TextColorControlProps {
  /** The open session's TipTap instance, or null when none is open. */
  editor: Editor | null;
  /** The selected block the palette styles when no session is open. */
  block: BlockInfo | null;
  /** The preview container, measured for the colour a block renders in. */
  previewRef: RefObject<HTMLDivElement>;
  /** Blocks editing (no target, or an upload is running). */
  disabled: boolean;
  /** Writes one inline-style property on the selected block. */
  onStyleChange: (property: StyleProperty, value: string) => void;
  /** Commits the session, for when focus leaves it from the popover. */
  onCommitSession: () => void;
}

/**
 * The toolbar's text-colour control, targeting whatever the author is on: an
 * "A" whose underline shows the colour the target actually renders in - the
 * selected runs' own colour in a text session, the selected block's authored
 * or computed colour otherwise - fronting the palette popover. Applying writes
 * an inline mark on the session selection, or the block's colour style;
 * "Default" clears either back to what it inherits.
 * @param editor - The open session's TipTap instance, or null
 * @param block - The selected block styled when no session is open
 * @param previewRef - The preview container, measured for the rendered colour
 * @param disabled - Blocks editing
 * @param onStyleChange - Writes one inline-style property on the block
 * @param onCommitSession - Commits the session
 * @returns The text-colour control
 */
export function TextColorControl({
  editor,
  block,
  previewRef,
  disabled,
  onStyleChange,
  onCommitSession,
}: TextColorControlProps) {
  const [open, setOpen] = useState(false);
  const authoredColor = block?.styles.color ?? "";
  const renderedColor = useRenderedStyle(
    previewRef,
    editor === null ? (block?.nodeId ?? null) : null,
    "color",
    authoredColor,
  );
  const color =
    editor !== null ? effectiveColor(editor) : authoredColor || renderedColor;

  const apply = (next: string | null, keepFocus: boolean) => {
    setOpen(false);
    if (editor !== null) {
      const chain = editor.chain();
      (keepFocus ? chain.focus() : chain).setTextStyleColor(next).run();
      return;
    }
    if (block !== null) {
      onStyleChange("color", next ?? "");
    }
  };

  // Live path for the platform picker: paints the target on every drag tick
  // without closing the popover or moving focus, so the author sees the colour
  // land as they pick it. Style-knob history coalescing keeps the ticks from
  // piling up as undo entries.
  const preview = (next: string) => {
    if (editor !== null) {
      editor.chain().setTextStyleColor(next).run();
      return;
    }
    if (block !== null) {
      onStyleChange("color", next);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        type="button"
        title="Text color"
        aria-label="Text color"
        disabled={disabled}
        className={cn(
          "flex size-8 shrink-0 flex-col items-center justify-center rounded-md transition-colors",
          disabled ? "opacity-50" : "hover:bg-muted data-[state=open]:bg-muted",
        )}
      >
        <span className="text-sm font-medium leading-none">A</span>
        <span
          className={cn(
            "mt-0.5 h-1 w-5 rounded-[2px]",
            color === "" && "bg-foreground",
          )}
          style={color === "" ? undefined : { backgroundColor: color }}
        />
      </PopoverTrigger>
      <PopoverContent
        {...TEXT_SESSION_HOLD_PROPS}
        align="start"
        className="w-auto p-2"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <TextColorPicker
          editor={editor}
          value={color}
          onApply={apply}
          onPreview={preview}
          onCommitSession={onCommitSession}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * The colour the selection renders in: its own when its runs agree on one,
 * otherwise the colour the browser paints it with.
 * @param editor - The open session's TipTap instance
 * @returns The CSS colour, or "" when it can't be measured
 */
function effectiveColor(editor: Editor): string {
  const selection = readTextStyleSelection(editor, "color");
  const own = selection.state === "uniform" ? selection.value : null;
  return own ?? computedSelectionStyle(editor, "color");
}
